import { uniqBy } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { usePageTitle } from '../../hooks/usePageTitle'
import { BaseRecord } from '../../models/BaseRecord'
import { PSComponent } from '../../models/PSComponent'
import { RecordFactory } from '../../models/RecordFactory'
import { Report } from '../../models/Report'
import { ReportResult } from '../../models/ReportResult'
import { ReportTotal } from '../../models/ReportTotal'
import { Subaccount } from '../../models/Subaccount'
import { User } from '../../models/User'
import { RecordNotFoundError } from '../../models/errors/RecordNotFoundError'
import { SESSION_STORAGE_RETURNED_KEY } from '../../utils/constants'
import { currencyFormat } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import ReportDetailsTable from './ReportDetailsTable'
import { ReportIcon } from './ReportIcon'
import { REPORTS_PATH } from './ReportRunnerPage'

type DeltaSubtitleProps = {
    total?: ReportTotal
    fieldType?: string
    onlyReturnPercent?: boolean
}

export const DeltaSubtitle: React.FC<DeltaSubtitleProps> = ({
    total,
    fieldType,
    onlyReturnPercent,
}) => {
    const data = {
        image: '',
        firstPart: '',
        lastPart: '',
        color: '',
    }
    let deltaTotal = total?.deltaTotal
    let percentData = total?.deltaString
    percentData = percentData ? percentData.replace('-', '').replace('+', '') : undefined
    if (total !== undefined && fieldType !== undefined) {
        if (fieldType === 'currency') {
            deltaTotal =
                total.deltaTotal !== undefined ? currencyFormat(total.deltaTotal, true) : undefined
        }
        else if (fieldType === 'integer') {
            deltaTotal =
                total.deltaTotal !== undefined ? parseInt(total.deltaTotal).toString() : undefined
        }
        else if (fieldType === 'percent') {
            deltaTotal = undefined
        }
    }
    if (total?.delta && total.delta > 0 && percentData) {
        if (deltaTotal && !onlyReturnPercent) {
            data.firstPart = `+${deltaTotal} (`
            data.lastPart = `${percentData})`
        }
        else {
            data.firstPart = ''
            data.lastPart = percentData
        }
        data.image = '↑ '
        data.color = 'text-[#179b42]'
    }
    else if (total?.delta && total.delta < 0 && percentData) {
        if (deltaTotal && !onlyReturnPercent) {
            data.firstPart = `-${deltaTotal} (`
            data.lastPart = `${percentData})`
        }
        else {
            data.firstPart = ''
            data.lastPart = percentData
        }
        data.image = '↓ '
        data.color = 'text-[#e22216]'
    }
    else {
        data.firstPart = '0.0%'
        data.lastPart = ''
        data.color = 'text-gray-500'
    }

    return (
        <div className={`text-paragraph-sm font-bold ${data.color}`}>
            {data.image ? data.image : ''}
            {data.firstPart}
            {data.lastPart}
        </div>
    )
}

type ReportDetailsData = {
    report: Report
    measure: PSComponent
    measureTotal: ReportTotal
    values: ReportResult[]
    detailsTotal: ReportTotal
    subtitle: string
    dateDisplay: string
    icon?: React.ReactElement
    person?: User
    subaccount?: Subaccount
    tableRowsData: ReportResult[]
    nextPage: number
    returned: boolean
}

export type ReportDetailsLocationProps = {
    activeReport: Report
    measure: PSComponent
    measureTotal: ReportTotal
    values: ReportResult[]
    person?: User
    subaccount?: Subaccount
    tableRowsData?: ReportResult[]
    nextPage?: number
}

const viewWillAppear = async (
    base: PocketComponent
): Promise<ReportDetailsData | RecordNotFoundError> => {
    const person = !!base.locationState.person
        ? BaseRecord.fromSerializer(base.locationState.person, User)
        : undefined

    const subaccount = !!base.locationState.subaccount
        ? BaseRecord.fromSerializer(base.locationState.subaccount, Subaccount)
        : undefined

    if (!base.locationState.activeReport || !base.locationState.measure) {
        return new RecordNotFoundError('Report not found')
    }

    const report = BaseRecord.fromSerializer(base.locationState.activeReport, Report)
    const measureTotal = BaseRecord.fromSerializer(base.locationState.measureTotal, ReportTotal)

    let detailsTotal = measureTotal
    let subTitle = 'Total'
    let icon: React.ReactElement | undefined

    const values: ReportResult[] = base.locationState.values.map((result: any) =>
        BaseRecord.fromSerializer(result, ReportResult)
    )

    if (values.length) {
        if (values.length === 2) {
            subTitle = `${values[0].title} → ${values[1].title}`
            detailsTotal = values[1].total!
        }
        else {
            subTitle = values[0].title!
            detailsTotal = values[0].total!
        }
        icon = <ReportIcon result={values[0]} />
    }

    const returned = sessionStorage.getItem(SESSION_STORAGE_RETURNED_KEY) === 'true'
    // if (returned) {
    //     // Don't load from location state if refreshed
    //     sessionStorage.removeItem(SESSION_STORAGE_RETURNED_KEY)
    // }

    const tableRowsData: ReportResult[] = (
        returned && !!base.locationState.tableRowsData ? base.locationState.tableRowsData : []
    ).map((data: any) => BaseRecord.fromSerializer(data, ReportResult))

    const nextPage: number =
        returned && base.locationState.nextPage ? base.locationState.nextPage : 0

    return {
        report: report,
        measure: BaseRecord.fromSerializer(base.locationState.measure, PSComponent),
        measureTotal: measureTotal,
        values: values,
        detailsTotal: detailsTotal,
        subtitle: subTitle,
        dateDisplay: report.dateDisplayString,
        icon: icon,
        person: person,
        subaccount: subaccount,
        tableRowsData: tableRowsData,
        nextPage: nextPage,
        returned: returned,
    }
}

const render = ({ data, base }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    if (data instanceof RecordNotFoundError) {
        throw data
    }

    if (!data) return null

    usePageTitle('Reports')

    if (data.returned && sessionStorage.getItem(SESSION_STORAGE_RETURNED_KEY) === 'true') {
        // Don't load from saved state if returned from details page and the component is refreshed
        sessionStorage.removeItem(SESSION_STORAGE_RETURNED_KEY)
    }

    const navigate = usePSNavigate()

    const [tableRowsDataState, setTableRowsDataState] = useState<ReportResult[]>(data.tableRowsData)
    const [nextPageState, setNextPageState] = useState<number>(data.nextPage)

    const [transactionCount, setTransactionCount] = useState<number | undefined>(undefined)

    useEffect(() => {
        if (nextPageState === 0) {
            didPage()
        }
    }, [])

    async function didPage() {
        if (data instanceof RecordNotFoundError) {
            return
        }

        base.showProgressBar('Loading..')
        const result = await RecordFactory.getInstance().runDetailReport(
            data.measure.id!,
            data.values.length ? data.report.dimensions : undefined,
            data.values,
            data.report.criterias,
            undefined,
            data.report.dateType === 'custom' ? data.report.date : undefined,
            data.report.dateType === 'custom' ? data.report.endDate : undefined,
            data.report.dateType,
            data.report.dateCompareType,
            nextPageState,
            data.person,
            data.subaccount
        )
        if (result.ok) {
            const results = result.value
            const total = results.total as ReportTotal
            const newItems = results.results
            const dataCount = tableRowsDataState.length + newItems.length
            setTableRowsDataState((prev) => uniqBy([...prev, ...newItems], (row) => row.id))
            setTransactionCount(results.subtitle)
            if (!newItems.length || total.count! <= dataCount) {
                setNextPageState(-1)
            }
            else {
                setNextPageState((prev) => prev + 1)
            }
            base.hideProgressBar()
        }
        else {
            base.hideProgressBar()
            base.logAndDisplayError(result.error)
        }
    }

    useEffect(() => {
        const mainElement = document.querySelector('main')
        if (!mainElement) return

        function handleScroll() {
            if (mainElement) {
                if (
                    mainElement.scrollHeight - mainElement.scrollTop === mainElement.clientHeight &&
                    nextPageState !== -1
                ) {
                    const previousScrollHeight = mainElement.scrollHeight
                    const previousScrollTop = mainElement.scrollTop
                    didPage().then(() => {
                        mainElement.scrollTop =
                            previousScrollTop + (mainElement.scrollHeight - previousScrollHeight)
                    })
                }
            }
        }

        mainElement.addEventListener('scroll', handleScroll)
        return () => mainElement.removeEventListener('scroll', handleScroll)
    }, [nextPageState])

    const onSelectRow = useCallback(
        (result: ReportResult) => {
            if (base.me.plan === 'franchise') return

            const navOptions = {
                state: {
                    activeReport: data.report,
                    measure: data.measure,
                    measureTotal: data.measureTotal,
                    values: data.values,
                    person: data.person,
                    subaccount: data.subaccount,
                    tableRowsData: tableRowsDataState,
                    nextPage: nextPageState,
                    reportData: tableRowsDataState,
                    returnAddress: window.location.pathname,
                },
            }

            let type = result.type
            if (type === 'refund') {
                type = 'payment'
            }

            if (!type) {
                return
            }

            switch (type) {
                case 'booking': {
                    navigate(`/bookings/${result.id}`, navOptions)
                    break
                }
                case 'invoice': {
                    navigate(`/invoices/${result.id}`, navOptions)
                    break
                }
                case 'package': {
                    navigate(`/packages/${result.id}`, navOptions)
                    break
                }
                case 'gift': {
                    navigate(`/gifts/${result.id}`, navOptions)
                    break
                }
                case 'lesson': {
                    navigate(`/lessons/${result.id}`, navOptions)
                    break
                }
                case 'payment': {
                    navigate(`/payments/${result.id}`, navOptions)
                    break
                }
                case 'estimate': {
                    navigate(`/estimates/${result.id}`, navOptions)
                    break
                }
                case 'subscription': {
                    navigate(`/subscriptions/${result.id}`, navOptions)
                    break
                }
                case 'charge': {
                    navigate(`/charges/${result.id}`, navOptions)
                    break
                }
                case 'client': {
                    navigate(`/clients/${result.id}`, navOptions)
                    break
                }
            }
        },
        [nextPageState, tableRowsDataState]
    )

    const onBack = useCallback(() => {
        sessionStorage.setItem(SESSION_STORAGE_RETURNED_KEY, 'true')
        const path = `${REPORTS_PATH}/${data.report.id}`
        navigate(data.person ? `${path}?clientId=${data.person.id}` : path)
    }, [data, navigate])

    useEffect(() => {
        window.addEventListener('popstate', onBack)
        return () => {
            window.removeEventListener('popstate', onBack)
        }
    }, [onBack])

    return (
        <div className="reporting details">
            <div className="topBar text-primary-700 focus-visible:ring-info-400 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden">
                <button className="back" onClick={onBack} tabIndex={0}>
                    <span className="chevron">&lt;</span>
                    <span className="title">{data.measure.title}</span>
                </button>
            </div>
            <div className="header">
                <div className="titleGroup">
                    <div className="text-heading-sm font-bold text-gray-700">
                        {data.measure.title}
                    </div>
                    {transactionCount !== undefined && (
                        <div className="text-label-md font-bold text-gray-600">{`(${transactionCount})`}</div>
                    )}
                </div>
            </div>
            <div className="body">
                <div className="detailCard">
                    {!!data.icon && <div className="icon">{data.icon}</div>}
                    <div className="body">
                        <div className="text-label-md text-gray-600 capitalize">
                            {data.subtitle}
                        </div>
                        <div className="text-heading-sm font-bold text-gray-700">
                            {data.detailsTotal.totalString}
                        </div>
                        {data.report.dateType !== 'custom' && (
                            <DeltaSubtitle
                                total={data.detailsTotal}
                                fieldType={data.measure.fieldType}
                            />
                        )}
                        {data.report.dateType !== 'custom' && (
                            <div className="text-label-sm text-gray-600 capitalize">
                                {data.dateDisplay}
                            </div>
                        )}
                    </div>
                </div>
                <div className="detailsTable">
                    <ReportDetailsTable
                        rows={tableRowsDataState}
                        metricName={data.measure.title!}
                        onClick={onSelectRow}
                    />
                </div>
            </div>
        </div>
    )
}

export const ReportDetailsPage = usePSComponent(render, viewWillAppear)
